import { motion } from "framer-motion";
import { Phone, MapPin } from "lucide-react";
import { SITE, TEL_HREF } from "@/siteConfig";

const HERO_IMAGE = {
  url: "/images/hero.webp",
  alt: "Cancello automatico scorrevole con motorizzazione su binario",
};

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden pt-28 pb-14 sm:pt-36 sm:pb-20"
  >
    <div className="hero-grid-bg pointer-events-none absolute inset-0" />
    <div className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-[120px]" />

    <div className="relative mx-auto grid max-w-7xl grid-cols-12 items-center gap-y-10 px-4 sm:px-6 lg:gap-x-10 lg:px-8">
      <div className="col-span-12 min-w-0 lg:col-span-7">
        <div className="mb-6 inline-flex max-w-full items-center gap-2.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" />
          <span className="font-tech text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.25em] text-amber-300">
            Pronto intervento H24 · Anche di notte
          </span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-zinc-50">
          <Line delay={0.08}>Pronto intervento</Line>
          <Line delay={0.18}>
            <span className="text-amber-400">H24</span> a Roma
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
        >
          Cancelli, serrande, automazioni, serrature e interventi elettrici.
          Assistenza urgente a Roma, anche di notte.
        </motion.p>

        <div className="mt-8">
          <a
            href={TEL_HREF}
            data-testid="hero-call-now-button"
            className="flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-amber-400 px-9 py-4 text-base sm:text-lg font-extrabold uppercase tracking-wide text-zinc-950 transition-colors hover:bg-amber-300 active:scale-[0.98]"
          >
            <Phone className="h-5 w-5" />
            Chiama ora — {SITE.phoneDisplay}
          </a>
        </div>

        <p className="mt-6 flex items-center gap-2 text-sm text-zinc-500">
          <MapPin className="h-4 w-4 text-amber-400/80" />
          Roma e provincia
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="col-span-12 min-w-0 lg:col-span-5"
      >
        <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
          <img
            src={HERO_IMAGE.url}
            alt={HERO_IMAGE.alt}
            className="h-[340px] sm:h-[440px] w-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl border border-white/10 bg-[#0A0D12]/80 px-4 py-3 backdrop-blur-md">
            <div>
              <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-amber-400">
                Linea diretta H24
              </p>
              <p className="font-display text-lg font-bold text-zinc-100">
                {SITE.phoneDisplay}
              </p>
            </div>
            <a
              href={TEL_HREF}
              data-testid="hero-card-call-button"
              aria-label={`Chiama il ${SITE.phoneDisplay}`}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400 text-zinc-950 transition-colors hover:bg-amber-300"
            >
              <Phone className="h-5 w-5" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);
